import React, { useState, useEffect, useRef } from "react";

const typeIcon = (type) => {
  if (type.startsWith("signalsync")) return "⚡";
  if (type === "new_follower") return "👥";
  if (type === "follower_stopped") return "👥";
  if (type === "high_drawdown") return "⚠️";
  if (type.startsWith("alphagrade")) return type.includes("up") ? "📈" : "📉";
  if (type === "ea_disconnected") return "🔌";
  if (type.startsWith("plan_trader")) return "⚡";
  return "🔔";
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotifs = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await fetch("/api/notifications", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) {
        setNotifs(data.notifications || []);
        setUnread(data.unread || 0);
      }
    } catch {}
  };

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleOpen = async () => {
    setOpen((v) => !v);
    if (!open && unread > 0) {
      try {
        const token = localStorage.getItem("token");
        await fetch("/api/notifications/read-all", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        setUnread(0);
        setNotifs((prev) => prev.map((n) => ({ ...n, isRead: true })));
      } catch {}
    }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={handleOpen}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "6px 8px",
          borderRadius: 8,
          position: "relative",
          display: "flex",
          alignItems: "center",
          color: "var(--text-muted)",
          transition: "color 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-main)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
        title="Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span style={{
            position: "absolute",
            top: 4, right: 4,
            background: "var(--danger)",
            color: "#fff",
            borderRadius: "50%",
            width: 16, height: 16,
            fontSize: 10,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute",
          right: 0, top: "calc(100% + 8px)",
          width: 340,
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          zIndex: 1000,
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-main)" }}>
              Notifications
            </span>
            {notifs.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
                {notifs.filter((n) => !n.isRead).length === 0 ? "All read" : `${notifs.filter((n) => !n.isRead).length} unread`}
              </span>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {notifs.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>
                No notifications yet
              </div>
            ) : (
              notifs.map((n) => (
                <div key={n.id} style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--border-subtle)",
                  background: n.isRead ? "transparent" : "var(--accent-soft)",
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                    {typeIcon(n.type)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: n.isRead ? 400 : 600,
                      color: "var(--text-main)",
                      marginBottom: 2,
                    }}>
                      {n.title}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      lineHeight: 1.4,
                      marginBottom: 4,
                    }}>
                      {n.message}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-faint)" }}>
                      {n.createdAt}
                    </div>
                  </div>
                  {!n.isRead && (
                    <div style={{
                      width: 6, height: 6,
                      borderRadius: "50%",
                      background: "var(--accent)",
                      flexShrink: 0,
                      marginTop: 4,
                    }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
